import { Color, Spacing } from '@persona-id/design-tokens';
import { CircleInfoIcon } from '@persona-id/design-tokens/icons/16px';
import { ChevronUpIcon } from '@persona-id/design-tokens/icons/24px';
import produce from 'immer';
import { get, toPairs, uniq } from 'lodash/fp';
import { memo, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { change } from 'redux-form';
import styled, { css } from 'styled-components';

import {
  INQUIRY_TEMPLATE_EDITOR_FORM_NAME,
  selector,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { findComponentConfigPath } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/utils';
import { ScreenEditorComponentConfigTypes } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-constants';
import TranslateAllButton from 'dashboard/apps/InquiryTemplateEditor/TranslateAllButton';
import { TranslationInputGroup } from 'dashboard/apps/InquiryTemplateEditor/TranslationInputs/TranslationInputs';
import { getStepConfigAndPath } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import { buildLinkedStepElement } from 'dashboard/components/ConnectedInquiryRunTimelineCard/InquiryRunEventTimeline';
import TableLayout, { TableNullState, TableTD, TableTH } from 'dashboard/layouts/TableLayout';
import useTranslations, { TranslationRequests } from 'dashboard/lib/hooks/useTranslations';
import { titleCase } from 'dashboard/lib/utils/stringFormat';
import {
  InquiryTemplateVersion,
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepUi,
  isUiStepConfig,
} from 'dashboard/models/inquiry-template-version';
import { RootState } from 'dashboard/redux/store';
import { Z_INDICES } from 'dashboard/styles/variables';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker/Sticker';
import { Label, Paragraph } from 'design-system/components/Text';
import { TransitionDuration } from 'design-system/variables/transitions';
import { INQUIRY_LANGUAGES_ENGLISH, InquiryIso6391LanguageCode } from 'lib/constants/locales';
import useToggle from 'lib/hooks/useToggle';
import {
  isUiFormFieldOptionComponentConfig,
  Translation,
  UiComponentConfig,
  UiComponentConfigType,
} from 'lib/interfaces/ui-components';

export const LOCALE_TO_EXCLUDE_TRANSLATIONS = 'ur';
export const UNSUPPORTED_LANGUAGE_TEXT =
  'Translation auto-generation is currently not supported for ' +
  `${INQUIRY_LANGUAGES_ENGLISH[LOCALE_TO_EXCLUDE_TRANSLATIONS as InquiryIso6391LanguageCode]}. ` +
  'Custom texts need to be configured manually.';

const form = INQUIRY_TEMPLATE_EDITOR_FORM_NAME;
const EMPTY_SOURCE_TEXT_LABEL = 'Empty';

export type MissingTranslationsMap = {
  [stepName: string]: {
    [componentName: string]: MissingTranslation[];
  };
};

type MissingTranslation = {
  'text-key': string;
  locale: InquiryIso6391LanguageCode;
  'source-text': string | undefined;
};

const toKebab = (str: string): string => str.replace(/_/g, '-');

const StyledTableTR = styled.tr`
  display: table-row;
  height: 100%;
  table-layout: fixed;
`;

const StyledTableTD = styled(TableTD)<{ width: string }>`
  width: ${({ width }) => width};
`;

interface TranslationRowProps {
  locale: InquiryIso6391LanguageCode;
  localeLabel: (typeof INQUIRY_LANGUAGES_ENGLISH)[keyof typeof INQUIRY_LANGUAGES_ENGLISH];
  componentDisplayName: string;
  sourceText: string;
  currentValue: Translation['value'] | undefined;
  stepsConfigPath: string;
  disabled: boolean;
  handleUpdateTranslation: (translation: Translation, stepsConfigPath: string) => void;
}

const TranslationRow = memo(
  ({
    locale,
    localeLabel,
    componentDisplayName,
    sourceText,
    currentValue,
    stepsConfigPath,
    disabled,
    handleUpdateTranslation,
  }: TranslationRowProps) => {
    // needed to set value on auto-translate
    useEffect(() => {
      if (typeof currentValue === 'string') {
        setTranslationStateValue(currentValue);
      }
    }, [currentValue]);

    const hasEmptySourceText = sourceText == null || sourceText.length === 0;
    const [translationStateValue, setTranslationStateValue] = useState<string | undefined>(
      currentValue as string,
    );

    return (
      <StyledTableTR>
        <StyledTableTD width='5%'>{localeLabel}</StyledTableTD>
        <StyledTableTD width='15%'>{componentDisplayName}</StyledTableTD>
        <StyledTableTD
          width='40%'
          style={{ color: `${hasEmptySourceText ? Color.CoreSlate500 : 'inherit'}` }}
        >
          {hasEmptySourceText ? EMPTY_SOURCE_TEXT_LABEL : sourceText}
        </StyledTableTD>
        <StyledTableTD width='40%'>
          <TranslationInputGroup
            locale={locale}
            withLargeDisplay={sourceText?.length > 50}
            inputProps={{
              size: 'md',
              // Pass null placeholder when currentValue is empty string to give
              // visual indication of the change when the user auto-translates empty source text.
              placeholder: currentValue === '' ? undefined : 'Add missing translation',
              value: translationStateValue,
              onBlur: () => {
                if (translationStateValue != null) {
                  handleUpdateTranslation(
                    { locale, value: translationStateValue },
                    stepsConfigPath,
                  );
                }
              },
              onChange: (e) => setTranslationStateValue(e.target.value),
            }}
            translationSourceText={sourceText}
            disabled={disabled}
            onTranslationsRequest={(translation: string) =>
              handleUpdateTranslation({ locale, value: translation }, stepsConfigPath)
            }
          />
        </StyledTableTD>
      </StyledTableTR>
    );
  },
);
TranslationRow.displayName = 'TranslationRow';

const SectionHeaderContainer = styled.div`
  padding-top: ${Spacing[3]};
  padding-bottom: ${Spacing[3]};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.secondary};
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SectionSubtitle = styled.div`
  display: flex;
  align-items: center;
`;

const AnimatedChevron = styled(ChevronUpIcon)<{ open: boolean }>`
  height: ${Spacing[3]};
  transition: transform ${TransitionDuration.Fast};
  transform: rotate(0deg);
  ${(props) =>
    props.open &&
    css`
      transition: transform ${TransitionDuration.Fast};
      transform: rotate(180deg);
    `}
`;

const StyledTableLayout = styled(TableLayout)`
  margin-top: ${Spacing[5]};
`;

interface SectionHeaderProps {
  step: InquiryTemplateVersionStep;
  inquiryTemplateVersion: InquiryTemplateVersion;
  missingCount: number;
  defaultExpanded: boolean;
}

const SectionHeader = ({
  step,
  inquiryTemplateVersion,
  missingCount,
  defaultExpanded,
  children,
}: PropsWithChildren<SectionHeaderProps>) => {
  const [expanded, { toggle }] = useToggle(defaultExpanded);
  const templateId = inquiryTemplateVersion.relationships['inquiry-template'].data.id;
  const linkedStepElement = buildLinkedStepElement({
    stepName: step.name,
    step,
    templateId,
    versionId: inquiryTemplateVersion.id,
    editorSubPath: 'screens',
  });
  return (
    <>
      <SectionHeaderContainer>
        <SectionTitle id={step.name} onClick={toggle}>
          <SectionSubtitle>
            <AnimatedChevron open={expanded} />
            <Label size='md'>Screen: {linkedStepElement}</Label>
          </SectionSubtitle>
          {missingCount > 0 && <Sticker variant='danger'>{`${missingCount} Missing`}</Sticker>}
        </SectionTitle>
      </SectionHeaderContainer>
      {expanded && (
        <StyledTableLayout
          showHeader
          aria-role='treegrid'
          nullState={<TableNullState>No translations</TableNullState>}
          headerTR={
            <>
              <TableTH>
                <Label size='md'>Language</Label>
              </TableTH>
              <TableTH>
                <Label size='md'>Component</Label>
              </TableTH>
              <TableTH>
                <Label size='md'>Source (English)</Label>
              </TableTH>
              <TableTH>
                <Label size='md'>Translations</Label>
              </TableTH>
            </>
          }
        >
          {children}
        </StyledTableLayout>
      )}
    </>
  );
};

/**
 * Returns config and path for current component config data
 */
const getComponentConfigAndPath = (
  stepConfig: InquiryTemplateVersionStepUi,
  componentName: string,
): {
  componentConfig: UiComponentConfig;
  componentConfigPath: string;
} => {
  const componentConfigPath = (
    findComponentConfigPath(
      (componentConfig) => toKebab(componentConfig.name) === toKebab(componentName),
      stepConfig.config.components,
    ) ?? []
  ).join('.');
  const componentConfig = get(componentConfigPath, stepConfig.config.components);
  return { componentConfig, componentConfigPath };
};

const getTextKeyPath = (textKey: string, componentConfig: UiComponentConfig): string | null => {
  if (textKey.includes('option')) {
    if (!isUiFormFieldOptionComponentConfig(componentConfig)) return null;
    // options text keys have format 'option-option-value-Kebab-case'
    const optionValue = toKebab(textKey).split('-').slice(1).join('');
    const optionIndex = componentConfig.config.options.findIndex((option) => {
      const normalizedOptionValue = option.value
        // options can also have boolean values
        .toString()
        .replace(/([_-]|\s)/g, '')
        .toLowerCase();
      return normalizedOptionValue === optionValue.toLowerCase();
    });
    if (optionIndex === -1) return null;
    return `options.${optionIndex}.text`;
  }
  return textKey;
};

const getComponentDisplayName = (
  componentConfigType: UiComponentConfigType,
  textKey: string,
): string => {
  const componentDisplayText =
    ScreenEditorComponentConfigTypes[componentConfigType]?.displayName ?? 'Component';
  const attributeLabel = titleCase(textKey.replace(/([_-]|\s)/g, ' '));
  return componentDisplayText === attributeLabel
    ? componentDisplayText
    : `${componentDisplayText} (${attributeLabel})`;
};

export type MissingTranslationRowConfig = {
  configPath: string;
  sourceText: string;
  locale: InquiryIso6391LanguageCode;
  componentDisplayName: string;
  currentValue: Translation['value'] | undefined;
};

/**
 * flattens StepLocalizedString object to the following:
 * { stepName: Array<{ configPath, sourceText, locale, componentDisplayName, currentValue }> }
 * to more easily render missing translation rows and provide a more easily manipulatable data structure
 * for other usages within the component such as making bulk translation requests.
 */
const getMissingTranslationRowConfigs = (
  missingTranslationsMap: MissingTranslationsMap,
  steps: InquiryTemplateVersionStep[],
): Record<string, MissingTranslationRowConfig[]> => {
  return Object.entries(missingTranslationsMap).reduce(
    (rowConfigMap, [stepName, componentMissingMap]) => {
      const { stepConfig, stepConfigPath } = getStepConfigAndPath(
        steps,
        (step) => toKebab(step.name) === toKebab(stepName),
      );
      if (!isUiStepConfig(stepConfig)) return rowConfigMap;
      const rowConfigs = Object.entries(componentMissingMap)
        .flatMap(([componentName, missingTranslations]) => {
          const { componentConfig, componentConfigPath } = getComponentConfigAndPath(
            stepConfig,
            componentName,
          );
          return missingTranslations.map(
            ({ 'text-key': textKey, 'source-text': sourceText, locale }) => {
              const textKeyPath = getTextKeyPath(textKey, componentConfig);
              if (textKeyPath == null) return null;
              const configPath = `${stepConfigPath}.config.components.${componentConfigPath}.config.${textKeyPath}-translations`;
              const currentValue = ((get(configPath, steps) as Translation[] | null) ?? []).find(
                (t) => t.locale === locale,
              )?.value;
              const componentDisplayName = getComponentDisplayName(componentConfig.type, textKey);
              return { configPath, sourceText, locale, componentDisplayName, currentValue };
            },
          );
        })
        .filter(Boolean);
      return {
        ...rowConfigMap,
        [stepName]: rowConfigs,
      };
    },
    {},
  );
};

// key is config path to missing text attribute, e.g:
// '0.config.components.1.config.title-translations'
type TranslationRequestConfigMap = Record<
  string,
  { locales: InquiryIso6391LanguageCode[]; sourceText: string }
>;

/**
 * Helper which converts the missing translations object into a map which is used to keep track of the
 * total missing translations count display and to make the translate all request to google translate client.
 *
 * @param missingTranslations translations object with structure: { stepName: { locale: { componentName: { stringKey: null } } } }
 * @param steps steps config - used to construct redux-form path
 * @returns map of redux-form path to config needed to make translations request and update form values (locales, source text)
 */
export const getTranslationRequestConfigMap = (
  missingTranslationRowConfigs: Record<string, MissingTranslationRowConfig[]>,
): TranslationRequestConfigMap => {
  return Object.values(missingTranslationRowConfigs)
    .flatMap((rowConfigs) => rowConfigs)
    .reduce(
      (configMap: TranslationRequestConfigMap, { configPath, sourceText, locale }) =>
        produce(configMap, (draftMap) => {
          if (draftMap[configPath] != null) {
            draftMap[configPath].locales.push(locale);
          } else {
            draftMap[configPath] = {
              locales: [locale],
              sourceText,
            };
          }
        }),
      {},
    );
};

/**
 * reduce full map of missing translation configs to Array<[sourceText, locales[]]> for the
 * request payload, as many translations could be requested for the same source text.
 */
const getTranslationRequests = (
  translationRequestsConfigMap: TranslationRequestConfigMap,
): TranslationRequests => {
  return toPairs(
    Object.values(translationRequestsConfigMap).reduce(
      (map: Record<string, InquiryIso6391LanguageCode[]>, { locales, sourceText }) => ({
        ...map,
        [sourceText]: uniq([...(map[sourceText] ?? []), ...locales]),
      }),
      {},
    ),
  );
};

const MissingTranslationsBanner = styled.div`
  position: sticky;
  top: 0px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: ${Spacing[9]};
  padding: ${Spacing[4]};
  z-index: ${Z_INDICES.banner};
  background-color: ${({ theme }) => theme.color.variants.caution.highlightLight};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  margin-bottom: ${Spacing[2]};
`;

interface FormSectionTranslationsConfigProps {
  inquiryTemplateVersion: InquiryTemplateVersion;
  missingTranslations: MissingTranslationsMap;
  disabled?: boolean;
}

const FormSectionTranslationsConfig = ({
  inquiryTemplateVersion,
  missingTranslations,
  disabled = false,
}: FormSectionTranslationsConfigProps) => {
  // steps config data within editor form - used to accurately update translations
  const steps = useSelector<RootState, InquiryTemplateVersionStep[]>(
    (state) => selector(state, 'steps') ?? [],
  );

  const missingTranslationRowConfigs = useMemo<Record<string, MissingTranslationRowConfig[]>>(
    () => getMissingTranslationRowConfigs(missingTranslations, steps),
    [missingTranslations, steps],
  );
  const missingTranslationRowConfigsFlatMap = useMemo<MissingTranslationRowConfig[]>(
    () => Object.values(missingTranslationRowConfigs).flatMap((rowConfigs) => rowConfigs),
    [missingTranslationRowConfigs],
  );

  const missingTranslationsCount = useMemo<number>(
    () =>
      missingTranslationRowConfigsFlatMap.filter(({ currentValue }) => currentValue == null).length,
    [missingTranslationRowConfigsFlatMap],
  );
  const translateAllLabel = `Translate All (${missingTranslationsCount})`;

  const dispatch = useDispatch();
  const { loading, requestTranslations } = useTranslations();
  const handleTranslateAll = useCallback(async () => {
    // map with all data needed to make the request to google translate client and update
    // the form with the translated values - redux form path, source text, and locales.
    const translationRequestsConfigMap = getTranslationRequestConfigMap(
      missingTranslationRowConfigs,
    );
    const translations = await requestTranslations(
      getTranslationRequests(translationRequestsConfigMap),
    );
    batch(() => {
      Object.entries(translationRequestsConfigMap).forEach(
        ([configPath, { locales, sourceText }]) => {
          const newTranslations: Translation[] = locales
            .map((locale) => {
              const value = translations[sourceText]?.[locale];
              // Cannot assume that a translation exists because the API request could have failed
              if (value == null) return null;
              const newTranslation: Translation = {
                locale,
                value,
              };
              return newTranslation;
            })
            .filter((translation): translation is Translation => translation != null);
          const currentTranslations = get(configPath, steps) as Translation[] | undefined;
          const updatedTranslations = (currentTranslations ?? [])
            // omit any existing translations for locales included within missing translations
            .filter(({ locale }) => !locales.includes(locale))
            .concat(newTranslations);
          dispatch(change(form, `steps.${configPath}`, updatedTranslations));
        },
      );
    });
  }, [missingTranslationRowConfigs, steps, dispatch, requestTranslations]);

  const handleUpdateTranslation = useCallback(
    (newTranslation: Translation, stepsConfigPath: string) => {
      const currentTranslations = get(stepsConfigPath, steps) as Translation[] | undefined;
      const updatedTranslations = (currentTranslations ?? [])
        .filter(({ locale }) => locale !== newTranslation.locale)
        .concat(newTranslation);
      dispatch(change(form, `steps.${stepsConfigPath}`, updatedTranslations));
    },
    [steps, dispatch],
  );

  const shouldShowUnsupportedLanguageText = useMemo<boolean>(
    () =>
      missingTranslationRowConfigsFlatMap.filter(
        ({ locale }) => locale === LOCALE_TO_EXCLUDE_TRANSLATIONS,
      ).length > 0,
    [missingTranslationRowConfigsFlatMap],
  );
  const unsupportedLanguageText = shouldShowUnsupportedLanguageText
    ? UNSUPPORTED_LANGUAGE_TEXT
    : '';

  return (
    <Card>
      {missingTranslationsCount > 0 && (
        <MissingTranslationsBanner>
          <CircleInfoIcon />
          <Spacer height={0} width={1} />
          <Paragraph size='lg' color='primary'>
            You have
            <b>{` ${missingTranslationsCount} missing translations.`}</b>
            <br />
            {unsupportedLanguageText}
          </Paragraph>
          <TranslateAllButton
            id='form-section-translations-config__translate-all-button'
            variant='inverse'
            buttonLabel={translateAllLabel}
            hasMissingSourceText={false}
            isPending={loading}
            handleTranslateAll={handleTranslateAll}
          />
        </MissingTranslationsBanner>
      )}
      {Object.entries(missingTranslationRowConfigs).map(([stepName, rowConfigs], stepIndex) => {
        const { stepConfig } = getStepConfigAndPath(
          steps,
          (step) => toKebab(step.name) === toKebab(stepName),
        );
        if (!isUiStepConfig(stepConfig)) return null;
        const missingCount = rowConfigs.filter(({ currentValue }) => currentValue == null).length;
        return (
          <SectionHeader
            key={stepName}
            step={stepConfig}
            inquiryTemplateVersion={inquiryTemplateVersion}
            missingCount={missingCount}
            defaultExpanded={stepIndex === 0 || missingTranslationsCount < 10}
          >
            {rowConfigs.map(
              ({ configPath, sourceText, locale, componentDisplayName, currentValue }) => {
                return (
                  <TranslationRow
                    key={`${configPath}-${locale}`}
                    locale={locale}
                    localeLabel={INQUIRY_LANGUAGES_ENGLISH[locale]}
                    componentDisplayName={componentDisplayName}
                    sourceText={sourceText}
                    currentValue={currentValue}
                    stepsConfigPath={configPath}
                    disabled={disabled}
                    handleUpdateTranslation={handleUpdateTranslation}
                  />
                );
              },
            )}
          </SectionHeader>
        );
      })}
    </Card>
  );
};

export default FormSectionTranslationsConfig;
